import {
  Announcement,
  Business,
  BusinessCenter,
  DashboardCustomize,
  Chat,
  Event,
  History,
  Key,
  Link as LinkIcon,
  LocalOffer,
  Paid,
  Public,
  SelectAll,
  Settings,
  Star,
  WorkOutline,
  Payments,
  Group,
  ChecklistRtl,
  ImportExport,
  SupervisorAccount,
  AutoAwesome,
  Checklist,
  ShoppingBag,
  BarChart,
  Ballot,
  HowToVote
} from '@mui/icons-material';
import { LocalActivity } from '@mui/icons-material';
import { Icon } from '@mui/material';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { isCompanyUser } from '@thro-helpers/users';
import {
  ChatPermissions,
  EventPermissions,
  LeihBarPermissions,
  PermissionEnum
} from '@thro-types/access-handler';
import { User } from '@thro-types/users';

import { useTranslation } from 'react-i18next';

import { useSelfEntitlementGroupsQuery } from 'api/hooks/users/self';
import { SubjectType } from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import { canCreateInvoices, isFinanceUser } from 'app/containers/Billing/utils';
import {
  hasAtLeastOneLeihbarEntitlement,
  isLeihBarAdmin,
  isLeihBarLender
} from 'app/containers/LeihBar/helper';
import { ReactComponent as ModuleIcon } from 'assets/metro-books.svg';
import { ReactComponent as StudentJobSearch } from 'assets/studentJobSearch.svg';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';
import Roles from 'utils/enums/Roles';
import { FEATURE_FLAGS } from 'utils/featureFlags';
import { hasAtLeastOneActivityLogPermission, isAreaAdmin } from 'utils/permissionHelper';

export interface Menu {
  header?: string;
  items: MenuItem[];
  show: () => boolean;
}

export interface MenuItem {
  icon: JSX.Element;
  title: string;
  url?: string;
  active?: (pathname: string) => boolean;
  show: () => boolean;
  external?: boolean;
  items?: Array<
    Required<Pick<MenuItem, 'icon' | 'title' | 'url' | 'show'>> & {
      external?: boolean;
    }
  >;

  disabled?: boolean;
  disabledMessage?: string;
}

export default function menuItems(user: User, userPermissions: PermissionEnum[]): Menu[] {
  const aclHelper = new ACLHelper(user);
  const { t } = useTranslation();
  const { data } = useSelfEntitlementGroupsQuery();
  const isBillingEnabled = useBooleanFlagValue(FEATURE_FLAGS.BILLING.ENABLED, true);
  const isLeihbarEnabled = useBooleanFlagValue(FEATURE_FLAGS.LEIHBAR.ENABLED, true);
  const isLeihbarStatisticsEnabled = useBooleanFlagValue(
    FEATURE_FLAGS.LEIHBAR.STATISTICS_ENABLED,
    true
  );
  const isPolyasEnabled = useBooleanFlagValue(FEATURE_FLAGS.POLYAS.ENABLED, false);

  const canAccessAPIKeys =
    aclHelper.isSuperUser() || userPermissions?.includes(ChatPermissions.AI_APIKEYS_READ);

  return [
    {
      header: `${user?.firstName} ${user?.lastName}`,
      show: () => !aclHelper.isCompanyUser() && !aclHelper.isInternationalOfficeUser(),
      items: [
        {
          icon: (
            <Icon className="fas fa-user-circle" style={{ width: 'auto', fontSize: '1.5em' }} />
          ),
          title: t(translations.layout.menu.profile()),
          url: '/profile',
          active: pathname => pathname === '/profile',
          show: () => !aclHelper.isCompanyUser()
        },
        {
          icon: <AutoAwesome />,
          title: canAccessAPIKeys
            ? t(translations.layout.menu.gki())
            : t(translations.layout.menu.gKIchat()),
          url: '/chat',
          active: pathname => pathname.startsWith('/chat'),
          show: () =>
            aclHelper.isSuperUser() ||
            !!userPermissions?.find(permission => permission === ChatPermissions.AI_CHAT_READ),

          items: canAccessAPIKeys
            ? [
                {
                  icon: <Chat />,
                  title: t(translations.layout.menu.chat()),
                  url: '/chat',
                  show: () => true
                },
                {
                  icon: <Key />,
                  title: t(translations.layout.menu.apiKeys()),
                  url: '/chat/api-keys',
                  show: () => true
                }
              ]
            : []
        }
      ]
    },
    {
      header: t(translations.layout.menu.leihbar()),
      show: () =>
        userPermissions.includes(LeihBarPermissions.LEIHBAR_PRODUCT_READ) && isLeihbarEnabled,
      items: [
        {
          icon: <ShoppingBag />,
          title: t(translations.layout.menu.leihbar()),
          url: '/leihbar',
          active: pathname =>
            pathname.startsWith('/leihbar') &&
            pathname !== '/leihbar/profile' &&
            !pathname.startsWith('/leihbar/daily') &&
            !pathname.startsWith('/leihbar/statistics') &&
            !pathname.startsWith('/leihbar/access'),
          show: () => userPermissions.includes(LeihBarPermissions.LEIHBAR_PRODUCT_READ)
        },
        {
          icon: <SupervisorAccount />,
          title: t(translations.layout.menu.leihbarProfile()),
          url: '/leihbar/profile',
          active: pathname => pathname === '/leihbar/profile',
          show: () =>
            hasAtLeastOneLeihbarEntitlement(data) &&
            (isLeihBarAdmin(userPermissions) || isLeihBarLender(userPermissions))
        },
        {
          icon: <Checklist />,
          title: t(translations.layout.menu.leihbarDaily()),
          url: '/leihbar/daily',
          active: pathname => pathname.startsWith('/leihbar/daily'),
          show: () =>
            hasAtLeastOneLeihbarEntitlement(data) &&
            (isLeihBarAdmin(userPermissions) || isLeihBarLender(userPermissions))
        },
        {
          icon: <BarChart />,
          title: t(translations.layout.menu.leihbarStatistics()),
          url: '/leihbar/statistics',
          active: pathname => pathname.startsWith('/leihbar/statistics'),
          show: () =>
            hasAtLeastOneLeihbarEntitlement(data) &&
            isLeihBarAdmin(userPermissions) &&
            isLeihbarStatisticsEnabled
        },
        {
          icon: <Group />,
          title: t(translations.layout.menu.leihbarAccess()),
          url: '/leihbar/access',
          active: pathname => pathname.startsWith('/leihbar/access'),
          show: () => isLeihBarAdmin(userPermissions)
        }
      ]
    },
    /**
     * COMMUNITY
     */
    {
      header: t(translations.layout.menu.community()),
      show: () => !aclHelper.isCompanyUser() && !aclHelper.isInternationalOfficeUser(),
      items: [
        {
          icon: <Icon className="far fa-newspaper" style={{ width: 'auto', fontSize: '1.3em' }} />,
          title: t(translations.layout.menu.articles()),
          url: '/articles',
          active: pathname => pathname.startsWith('/articles'),
          show: () => !aclHelper.isCompanyUser()
        },
        {
          icon: (
            <Icon className="far fa-calendar-alt" style={{ width: 'auto', fontSize: '1.55em' }} />
          ),
          title: t(translations.layout.menu.calendar()),
          url: '/calendar',
          active: pathname => pathname === '/calendar',
          show: () => !aclHelper.isCompanyUser()
        },
        {
          icon: <Event style={{ width: 'auto', fontSize: '1.55em' }} />,
          title: t(translations.layout.menu.events()),
          url: '/events',
          active: pathname => pathname.startsWith('/events'),
          show: () =>
            !!userPermissions?.find(permission => permission === EventPermissions.EVENTS_EVENT_READ)
        },
        {
          icon: <Icon className="fa fa-bullhorn" style={{ width: 'auto', fontSize: '1.35em' }} />,
          title: t(translations.layout.menu.bulletinBoard()),
          url: '/bulletin',
          active: pathname => pathname === '/bulletin',
          show: () => !aclHelper.isCompanyUser()
        },
        {
          icon: <Star />,
          title: 'AWPM',
          active: pathname => pathname.startsWith('/awpm'),
          show: () => aclHelper.isEmployee(),
          items: [
            {
              icon: (
                <Icon className="fas fa-list-alt" style={{ width: 'auto', fontSize: '1.4em' }} />
              ),
              title: t(translations.layout.menu.moduleSelections()),
              url: '/awpm/module-selections',
              show: () => aclHelper.isEmployee()
            },
            {
              icon: <ModuleIcon style={{ width: '22px' }} />,
              title: t(translations.layout.menu.modules()),
              url: '/awpm/modules',
              show: () => aclHelper.isSubjectSelectionAdmin(false, SubjectType.Awpm)
            },
            {
              icon: (
                <Icon className="fas fa-layer-group" style={{ width: 'auto', fontSize: '1.4em' }} />
              ),
              title: t(translations.layout.menu.groupings()),
              url: '/awpm/groupings',
              show: () => aclHelper.isSubjectSelectionAdmin(false, SubjectType.Awpm)
            }
          ]
        },
        {
          icon: <Star />,
          title: t(translations.layout.menu.titleAWPM()),
          url: '/awpm/student/module-selections',
          active: pathname => pathname.startsWith('/awpm'),
          show: () => aclHelper.isStudent(true)
        },
        {
          icon: <SelectAll />,
          title: 'FWPM',
          active: pathname => pathname.startsWith('/fwpm'),
          show: () => aclHelper.isEmployee(),
          items: [
            {
              icon: (
                <Icon className="fas fa-list-alt" style={{ width: 'auto', fontSize: '1.4em' }} />
              ),
              title: t(translations.layout.menu.moduleSelections()),
              url: '/fwpm/module-selections',
              show: () => aclHelper.isEmployee()
            },
            {
              icon: <ModuleIcon style={{ width: '22px' }} />,
              title: t(translations.layout.menu.modules()),
              url: '/fwpm/modules',
              show: () => aclHelper.isSubjectSelectionAdmin(false, SubjectType.Fwpm)
            }
          ]
        },
        {
          icon: <SelectAll />,
          title: t(translations.layout.menu.titleFWPM()),
          url: '/fwpm/student/module-selections',
          active: pathname => pathname.startsWith('/fwpm'),
          show: () => aclHelper.isStudent(true)
        }
      ]
    },
    /**
     * JOBMARKET
     */
    {
      header: t(translations.layout.menu.jobmarket()),
      show: () => true,
      items: [
        {
          icon: <BusinessCenter />,
          title: t(translations.layout.menu.jobs()),
          url: '/jobs',
          active: pathname =>
            pathname.startsWith('/jobs') &&
            pathname !== '/jobs/manage' &&
            pathname !== '/jobs/my' &&
            !pathname.startsWith('/jobs/studentOffers'),
          show: () => true
        },
        {
          icon: <StudentJobSearch />,
          title: aclHelper.isStudent(true)
            ? t(translations.layout.menu.myJobApplications())
            : t(translations.layout.menu.studentApplications()),
          url: '/jobs/studentOffers',
          active: pathname => pathname.startsWith('/jobs/studentOffers'),
          show: () => true
        },
        {
          icon: <WorkOutline />,
          title: t(translations.layout.menu.manageJobs()),
          url: '/jobs/manage',
          active: pathname => pathname === '/jobs/manage',
          show: () => aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)
        },
        {
          icon: <WorkOutline />,
          title: t(translations.layout.menu.manageMyJobs()),
          url: '/jobs/my',
          active: pathname => pathname === '/jobs/my',
          show: () =>
            aclHelper.isEmployee() && !aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)
        },
        {
          icon: <Business />,
          title: aclHelper.isJobmarketAdmin()
            ? t(translations.layout.menu.manageCompanies())
            : t(translations.common.companies()),
          url: '/companies',
          active: pathname =>
            pathname.startsWith('/companies') &&
            pathname !== '/companies/invoicing' &&
            pathname !== '/companies/university',
          show: () => !aclHelper.isCompanyUser()
        },
        {
          icon: <Business />,
          title: t(translations.layout.menu.myCompany()),
          url: `/companies/${isCompanyUser(user) ? user.company?.id : ''}`,
          active: pathname => /^\/companies\/\d*$/.test(pathname),
          show: () => aclHelper.isCompanyUser()
        },
        {
          icon: <Business />,
          title: t(translations.layout.menu.myCompany()),
          url: `/companies/university`,
          active: pathname => pathname === '/companies/university',
          show: () => aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)
        },
        {
          icon: <Icon className="fas fa-money-bill" style={{ width: 'auto' }} />,
          title: t(translations.layout.menu.myInvoices()),
          url: `/companies/${isCompanyUser(user) ? user.company?.id : ''}/invoices`,
          active: pathname => /^\/companies\/\d*\/invoices$/.test(pathname),
          show: () => aclHelper.isCompanyUser() && !aclHelper.isUniversityCompanyUser()
        },
        {
          icon: <Icon className="fas fa-money-bill" style={{ width: 'auto', fontSize: '1.2em' }} />,
          title: t(translations.layout.menu.manageInvoices()),
          url: '/companies/invoicing',
          active: pathname => pathname === '/companies/invoicing',
          show: () => aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)
        },
        {
          icon: <LocalOffer style={{ width: 'auto', fontSize: '1.5em' }} />,
          title: t(translations.layout.menu.pricingtable()),
          url: `${window.location.origin.toString()}/jobs/pricing`,
          external: true,
          show: () =>
            aclHelper.isCompanyUser() || aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)
        }
      ]
    },
    /**
     * INTERNATIONAL OFFICE
     */
    {
      show: () => aclHelper.isIncomingsAdmin() || aclHelper.isInternationalOfficeUser(),
      header: t(translations.layout.menu.internationalOffice()),
      items: [
        {
          icon: <Icon className="fas fa-users" style={{ width: 'auto', fontSize: '1.1em' }} />,
          title: t(translations.pages.internationalOffice.manageUsers()),
          url: '/io/users',

          active: pathname => pathname === '/io/users',
          show: () => aclHelper.isIncomingsAdmin()
        },
        {
          icon: <Icon className="fas fa-book" style={{ width: 'auto', fontSize: '1.3em' }} />,
          title: aclHelper.isIncomingsAdmin()
            ? t(translations.pages.internationalOffice.manageCourses())
            : t(translations.pages.internationalOffice.coursesTitle()),
          url: '/io/courses',

          active: pathname => pathname === '/io/courses',
          show: () => aclHelper.isIncomingsAdmin() || aclHelper.isInternationalOfficeUser()
        },
        {
          icon: (
            <Icon className="fas fa-calendar-alt" style={{ width: 'auto', fontSize: '1.3em' }} />
          ),
          title: aclHelper.isIncomingsAdmin()
            ? t(translations.pages.internationalOffice.manageEvents())
            : t(translations.pages.internationalOffice.eventsTitle()),
          url: '/io/events',

          active: pathname => pathname === '/io/events',
          show: () => aclHelper.isIncomingsAdmin() || aclHelper.isInternationalOfficeUser()
        }
      ]
    },
    /**
     * POLYAS
     * */
    {
      header: t(translations.layout.menu.polyas()),
      show: () => isPolyasEnabled,
      items: [
        {
          icon: <HowToVote />,
          title: t(translations.pages.polyas.menu.my()),
          url: '/election/my',
          active: pathname => pathname === '/election/my',
          show: () => true
        },
        {
          icon: <Ballot />,
          title: t(translations.pages.polyas.menu.admin()),
          url: '/election/admin',
          active: pathname => pathname.startsWith('/election/admin'),
          show: () => aclHelper.hasOne('entitlement', [Roles.POLYAS_ADMIN, Roles.SUPER_USER])
        }
      ]
    },
    /**
     * Billing
     */
    {
      header: t(translations.layout.menu.billing()),
      show: () => canCreateInvoices(user, userPermissions) && isBillingEnabled,
      items: [
        {
          icon: <Payments style={{ width: 'auto', fontSize: '1.65em' }} />,
          title: t(translations.pages.billing.title()),
          show: () => {
            return true;
          },
          active: pathname => pathname === '/billing' || pathname.startsWith('/billing/invoices'),
          url: '/billing'
        },
        {
          icon: <ChecklistRtl style={{ width: 'auto', fontSize: '1.65em' }} />,
          title: t(translations.layout.menu.billingApprove()),
          show: () => {
            return isFinanceUser(user, userPermissions);
          },
          active: pathname => pathname.startsWith('/billing/finance'),
          url: '/billing/finance'
        },
        {
          icon: <ImportExport style={{ width: 'auto', fontSize: '1.65em' }} />,
          title: t(translations.layout.menu.billingExport()),
          show: () => {
            return isFinanceUser(user, userPermissions);
          },
          active: pathname => pathname.startsWith('/billing/export'),
          url: '/billing/export'
        },
        {
          icon: <Group style={{ width: 'auto', fontSize: '1.65em' }} />,
          title: t(translations.pages.billing.customers.title()),
          show: () => {
            return true;
          },
          active: pathname => pathname.startsWith('/billing/customers'),
          url: '/billing/customers'
        },
        {
          icon: <Settings style={{ width: 'auto', fontSize: '1.5em' }} />,
          title: t(translations.layout.menu.billing()),
          url: '/billing/billing-settings',
          show: () => isFinanceUser(user, userPermissions),
          active: pathname => pathname.startsWith('/billing/billing-settings')
        }
      ]
    },
    /**
     * SETTINGS & LINKS
     */
    {
      header: t(translations.layout.menu.other()),
      show: () => !aclHelper.isCompanyUser(),
      items: [
        {
          icon: <History style={{ width: 'auto', fontSize: '1.5em' }} />,
          title: t(translations.layout.menu.activityLog()),
          url: '/activity-log',
          show: () =>
            aclHelper.isSuperUser() ||
            (isAreaAdmin(user, userPermissions) &&
              hasAtLeastOneActivityLogPermission(userPermissions)),
          active: pathname => pathname.startsWith('/activity-log')
        },
        {
          icon: <Icon className="fas fa-users" style={{ width: 'auto', fontSize: '1.3em' }} />,
          title: t(translations.layout.menu.shibUserManagement()),
          url: '/shib/users',
          show: () => aclHelper.isSuperUser() || aclHelper.isSelectionAdmin(),
          active: pathname => pathname.startsWith('/shib/users')
        },
        {
          icon: <Settings style={{ width: 'auto', fontSize: '1.65em' }} />,
          title: t(translations.layout.menu.settings()),
          active: pathname => pathname.startsWith('/settings') || pathname === '/test/users',
          show: () =>
            aclHelper.hasOne('entitlement', [
              Roles.JOBMARKET_ADMIN,
              Roles.SUPER_USER,
              Roles.INCOMINGS_ADMIN
            ]),
          items: [
            {
              icon: <WorkOutline style={{ width: 'auto', fontSize: '1.5em' }} />,
              title: t(translations.layout.menu.settingsJobmarket()),
              url: '/settings/jobmarket',
              show: () => aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)
            },
            {
              icon: (
                <Icon className="far fa-newspaper" style={{ width: 'auto', fontSize: '1.3em' }} />
              ),
              title: t(translations.layout.menu.settingsArticles()),
              url: '/settings/articles',
              show: () => aclHelper.isSuperUser()
            },
            {
              icon: (
                <Icon className="far fa-comments" style={{ width: 'auto', fontSize: '1.3em' }} />
              ),
              title: t(translations.layout.menu.settingsMessaging()),
              url: '/settings/messaging',
              show: () => aclHelper.isSuperUser()
            },
            {
              icon: <Icon className="fas fa-globe-europe" style={{ width: 'auto' }} />,
              title: t(translations.layout.menu.settingsInternationalOffice()),
              url: '/settings/io',
              show: () => aclHelper.isIncomingsAdmin() || aclHelper.isSuperUser()
            },
            {
              icon: <Icon className="fas fa-users" style={{ width: 'auto', fontSize: '1.3em' }} />,
              title: t(translations.layout.menu.testUserManagement()),
              url: '/test/users',
              show: () => aclHelper.isSuperUser()
            },
            {
              icon: <Announcement style={{ width: 'auto', fontSize: '1.5em' }} />,
              title: t(translations.layout.menu.settingsSystemNotifications()),
              url: '/settings/system-notifications',
              show: () => aclHelper.isSuperUser()
            },
            {
              icon: <Key style={{ width: 'auto', fontSize: '1.5em' }} />,
              title: t(translations.layout.menu.accessHandler()),
              url: '/settings/access-handler',
              show: () => aclHelper.isSuperUser()
            },
            {
              icon: <LocalActivity style={{ width: 'auto', fontSize: '1.5em' }} />,
              title: t(translations.layout.menu.eventsSettings()),
              url: '/settings/event',
              show: () => aclHelper.isSuperUser()
            },
            {
              icon: <DashboardCustomize style={{ width: 'auto', fontSize: '1.5em' }} />,
              title: t(translations.layout.menu.dashboardCardSettings()),
              url: '/settings/tile-settings',
              show: () => aclHelper.isSuperUser()
            }
          ]
        },
        {
          icon: <Public style={{ width: 'auto', fontSize: '1.6em' }} />,
          title: t(translations.layout.menu.links()),
          show: () => !aclHelper.isCompanyUser(),
          items: [
            {
              icon: <LinkIcon />,
              title: t(translations.pages.home.syncShare.title()),
              url: 'https://syncandshare.lrz.de',
              external: true,
              show: () => aclHelper.isStudent()
            },
            {
              icon: <LinkIcon />,
              title: t(translations.pages.home.library.title()),
              url: 'https://touchpoint.th-rosenheim.de/TouchPoint/start.do?View=fro',
              external: true,
              show: () => aclHelper.isStudent()
            },
            {
              icon: <LinkIcon />,
              title: t(translations.pages.home.learningCampus.title()),
              url: 'https://learning-campus.th-rosenheim.de/my/',
              external: true,
              show: () => true
            },
            {
              icon: <LinkIcon />,
              title: t(translations.pages.home.timeTable.title()),
              url: 'https://splan.fh-rosenheim.de/splan/',
              external: true,
              show: () => !aclHelper.isCompanyUser()
            },
            {
              icon: <LinkIcon />,
              title: t(translations.pages.home.onlineServiceCenter.title()),
              url: 'https://osc.th-rosenheim.de',
              external: true,
              show: () => aclHelper.isStudent() || aclHelper.isEmployee() || aclHelper.isSuperUser()
            },
            {
              icon: <LinkIcon />,
              title: t(translations.pages.home.documentResearch.title()),
              url: 'https://dms.fh-rosenheim.de',
              external: true,
              show: () => aclHelper.isEmployee()
            },
            {
              icon: <LinkIcon />,
              title: t(translations.pages.home.primion.title()),
              url: 'https://primion.fh-rosenheim.de/primeweb/',
              external: true,
              show: () => aclHelper.isEmployee()
            },
            {
              icon: <LinkIcon />,
              title: t(translations.pages.home.employeePortal.title()),
              url: 'https://www.mitarbeiterservice.bayern.de/',
              external: true,
              show: () => aclHelper.isEmployee()
            },
            {
              icon: <LinkIcon />,
              title: t(translations.pages.home.openCast.title()),
              url: 'https://vod.th-rosenheim.de',
              external: true,
              show: () => aclHelper.isEmployee()
            },
            {
              icon: <LinkIcon />,
              title: t(translations.pages.home.acquisition.title()),
              url: 'https://beschaffung.th-rosenheim.de',
              external: true,
              show: () => aclHelper.isEmployee()
            }
          ]
        }
      ]
    }
  ];
}
